/*
 * TWC account API wrapper — talks to the billing engine through the Shopify
 * app proxy (/apps/twc/v1/*). Plain ES2017+, no build step. The engine
 * envelope:
 *   200 -> { data: ... }        => resolves { ok:true, status, code:null, data }
 *   4xx -> { error: {code,message} } => { ok:false, status, code, message, data:null }
 *   202 -> { error: {code:'SIGNUP_PENDING'} } => { ok:false, status:202, code:'SIGNUP_PENDING', data:null }
 * Never throws; callers branch on r.ok / r.code.
 *
 * Modes:
 *  - storefront (default): base /apps/twc/v1, same-origin credentials; Shopify
 *    signs the request, engine resolves logged_in_customer_id.
 *  - live-local: localStorage.twc_api_base = http://localhost:3000/api/proxy/v1
 *    and localStorage.twc_dev_subscriber = <subscriberId> (engine dev bypass).
 *  - fixture: window.__TWC_FIXTURES bundle, or ?twc_fixture=<state> (harness
 *    fetches /dev/fixtures/<state>.json). GETs resolve from the bundle; POSTs
 *    resolve { ok:false, code:'FIXTURE_MODE' } so mutation UI can no-op.
 *  - preview: sessionStorage.twc_preview (Member 360's read-only "View My
 *    Account"). GETs carry ?twc_preview=<token>; every other method resolves
 *    { ok:false, code:'PREVIEW_READ_ONLY' } without a request. See below.
 */
(function (global) {
  'use strict';

  if (global.TwcAccountApi) return; // idempotent under duplicate script tags

  var FIXTURE_KEYS = {
    '/dashboard': 'dashboard',
    '/me': 'me',
    '/campaigns/current': 'campaigns_current',
    '/box/current': 'box_current',
    '/topup/current': 'topup_current',
    '/delivery-prefs': 'delivery_prefs',
    '/ballots': 'ballots',
    '/referrals': 'referrals',
    '/waitlist': 'waitlist',
    '/payment/methods': 'payment_methods',
    '/orders': 'orders',
  };

  /* One prefix rule, on purpose: an order detail path carries the order id
   * (`/orders/<id>`), thus it cannot have a key of its own in the table above.
   * Every GET below `/orders/` reads the one `orders_detail` fixture. */
  function fixtureKeyFor(path) {
    var key = FIXTURE_KEYS[path];
    if (key) return key;
    if (path.indexOf('/orders/') === 0) return 'orders_detail';
    return null;
  }

  var fixtureBundle = null; // { responses: { me: {status, body}, ... } }
  var fixtureLoading = null;

  function fixtureStateParam() {
    try {
      return new URLSearchParams(global.location.search).get('twc_fixture');
    } catch (e) {
      return null;
    }
  }

  // localStorage-persisted fixture bundle: lets a whole store-rendered page be
  // pinned to a canned state across reloads (design/QA passes on the real
  // theme). Set localStorage.twc_fixture_data to a bundle JSON; remove to unpin.
  function storedFixtureBundle() {
    try {
      var raw = global.localStorage.getItem('twc_fixture_data');
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function isFixtureMode() {
    // A preview tab always talks to the engine: no canned data.
    if (isPreview()) return false;
    return !!(global.__TWC_FIXTURES || fixtureStateParam() || storedFixtureBundle());
  }

  // Theme-editor scenario switching replaces the in-memory fixture atomically.
  // It is deliberately unavailable outside design mode: QA's query/storage
  // fixtures remain immutable for the lifetime of their page.
  function setDesignFixture(responses) {
    if (!(global.Shopify && global.Shopify.designMode)) return false;
    global.__TWC_FIXTURES = responses || {};
    fixtureBundle = { responses: global.__TWC_FIXTURES };
    fixtureLoading = null;
    return true;
  }

  function loadFixtureBundle() {
    if (global.__TWC_FIXTURES) {
      fixtureBundle = { responses: global.__TWC_FIXTURES };
      return Promise.resolve(fixtureBundle);
    }
    var stored = storedFixtureBundle();
    if (stored) {
      fixtureBundle = stored.responses ? stored : { responses: stored };
      return Promise.resolve(fixtureBundle);
    }
    if (fixtureBundle) return Promise.resolve(fixtureBundle);
    if (fixtureLoading) return fixtureLoading;
    var state = fixtureStateParam();
    fixtureLoading = fetch('/dev/fixtures/' + encodeURIComponent(state) + '.json')
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; })
      .then(function (json) {
        fixtureBundle = json && json.responses ? json : { responses: {} };
        return fixtureBundle;
      });
    return fixtureLoading;
  }

  function apiBase() {
    // A preview tab only ever asks the app proxy (no local overrides).
    if (isPreview()) return '/apps/twc/v1';
    if (global.__TWC_API_BASE) return global.__TWC_API_BASE;
    try {
      var v = global.localStorage.getItem('twc_api_base');
      if (v) return v;
    } catch (e) { /* storage unavailable */ }
    return '/apps/twc/v1';
  }

  function devSubscriber() {
    if (isPreview()) return null;
    try {
      return global.localStorage.getItem('twc_dev_subscriber');
    } catch (e) {
      return null;
    }
  }

  /* Read-only member account preview (Member 360 → "View My Account").
   *
   * snippets/twc-preview.liquid (part 'boot', the first TWC script in the
   * head) moves `#twc_preview=<token>` into this tab's sessionStorage and
   * strips the hash before anything else runs. capturePreviewHash() below is
   * the same step for a page that did not render that snippet (the harness);
   * on the storefront the hash is already gone when this file runs.
   *
   * While an entry exists the tab is a preview tab:
   *  - every engine GET carries `twc_preview=<token>`; the engine resolves the
   *    member from the token and refuses anything else. The token is opaque:
   *    nothing here parses it.
   *  - nothing but a GET leaves the browser (PREVIEW_READ_ONLY, with the
   *    read-only toast) — the engine refuses writes too, this is the first
   *    guard.
   *  - a 401, or a 403 PREVIEW_EXPIRED, never bounces to login: the bar says
   *    the preview expired. Past the stored expiry no request is sent at all.
   *  - no prefetch is consumed and no dev bypass header is sent.
   * The entry outlives its expiry on purpose: the tab stays a preview tab
   * until "Exit preview" clears it.
   */
  var PREVIEW_KEY = 'twc_preview';
  var PREVIEW_EXP_KEY = 'twc_preview_exp';
  var PREVIEW_TTL_MS = 10 * 60 * 1000;
  // Set on the first engine read that succeeds with the token. A token the
  // engine refuses before that never worked: the tab drops it and reloads
  // as a normal page (a crafted link cannot trap a visitor in a blank tab).
  var PREVIEW_OK_KEY = 'twc_preview_ok';
  // The engine's token shape (src/auth/preview-token.ts TOKEN_RE).
  var PREVIEW_TOKEN_RE = /^[A-Za-z0-9_-]{1,2048}$/;
  var PREVIEW_PAGE_RE = /^(\/[a-z]{2}(-[a-z]{2})?)?\/pages\/account-/i;
  var previewExpiredSeen = false;

  function capturePreviewHash() {
    try {
      var hash = global.location.hash || '';
      var match = /[#&]twc_preview=([^&]*)/.exec(hash);
      if (!match) return;
      // Same rules as the head boot: an account page only, the engine
      // token's shape only; anything else is stripped and ignored.
      var fresh = match[1];
      var store = global.sessionStorage;
      if (PREVIEW_PAGE_RE.test(global.location.pathname || '') && PREVIEW_TOKEN_RE.test(fresh) &&
          store.getItem(PREVIEW_KEY) !== fresh) {
        store.setItem(PREVIEW_KEY, fresh);
        store.setItem(PREVIEW_EXP_KEY, String(Date.now() + PREVIEW_TTL_MS));
        store.removeItem(PREVIEW_OK_KEY);
      }
      var rest = hash.slice(1).split('&').filter(function (part) {
        return part && part.indexOf('twc_preview=') !== 0;
      }).join('&');
      global.history.replaceState(global.history.state, '', global.location.pathname + global.location.search + (rest ? '#' + rest : ''));
    } catch (e) { /* storage blocked: no preview */ }
  }

  function previewEntry() {
    try {
      var token = global.sessionStorage.getItem(PREVIEW_KEY);
      if (!token) return null;
      var exp = Number(global.sessionStorage.getItem(PREVIEW_EXP_KEY)) || 0;
      return { token: token, expired: Date.now() >= exp };
    } catch (e) {
      return null;
    }
  }

  function isPreview() {
    return !!previewEntry();
  }

  function previewExpired() {
    var entry = previewEntry();
    return !!(entry && (entry.expired || previewExpiredSeen));
  }

  function previewCopy(key, fallback) {
    try {
      var el = global.document.querySelector('[data-twc-preview-i18n]');
      var copy = el ? JSON.parse(el.textContent) : null;
      if (copy && copy[key]) return decodeEntities(copy[key]);
    } catch (e) { /* fall back */ }
    return fallback;
  }

  function notifyPreviewExpired() {
    previewExpiredSeen = true;
    if (global.TWC_PREVIEW) global.TWC_PREVIEW.expired = true;
    try {
      global.document.dispatchEvent(new global.CustomEvent('twc:preview-expired'));
    } catch (e) { /* no event support: the bar reads expired() on its own */ }
  }

  function previewWorked() {
    try { return global.sessionStorage.getItem(PREVIEW_OK_KEY) === '1'; } catch (e) { return false; }
  }

  function markPreviewWorked() {
    try { global.sessionStorage.setItem(PREVIEW_OK_KEY, '1'); } catch (e) { /* storage blocked */ }
  }

  // A token that never worked: back to a normal page, no bar.
  function dropPreview() {
    clearPreview();
    try { global.location.reload(); } catch (e) { /* no reload: the page is inert */ }
  }

  function clearPreview() {
    try {
      global.sessionStorage.removeItem(PREVIEW_KEY);
      global.sessionStorage.removeItem(PREVIEW_EXP_KEY);
      global.sessionStorage.removeItem(PREVIEW_OK_KEY);
    } catch (e) { /* nothing stored */ }
    previewExpiredSeen = false;
    global.TWC_PREVIEW = undefined;
  }

  function readOnlyResult() {
    var message = previewCopy('read_only', 'Read-only preview');
    flash(message, 'info');
    return { ok: false, status: 0, code: 'PREVIEW_READ_ONLY', message: message, data: null };
  }

  capturePreviewHash();
  (function stampPreviewFlag() {
    var entry = previewEntry();
    if (entry && !(global.TWC_PREVIEW && global.TWC_PREVIEW.active)) {
      global.TWC_PREVIEW = { active: true, expired: entry.expired };
    }
  })();

  function routes() {
    return global.__TWC_ROUTES || {};
  }

  // The shared sign-in form with this page, query included, as a RELATIVE
  // return_to (the twc-redeem.js bounce). Shopify ignores return_url, so the
  // query (an email action link's ?twc_do=…&sku=…) used to be lost on the way
  // back from sign-in.
  function loginUrl() {
    var login = routes().loginBase || '/customer_authentication/login';
    // One leading slash, always: '//host/…' would be protocol-relative.
    var here = String(global.location.pathname || '/').replace(/^\/+/, '/') + global.location.search;
    return login + '?return_to=' + encodeURIComponent(here);
  }

  function redirectToLogin() {
    // Never navigate the theme editor's preview iframe (it renders as a 404)
    // or a fixture-pinned page; render the error state in place instead.
    if (global.Shopify && global.Shopify.designMode) return;
    if (isFixtureMode()) return;
    // A preview tab never leaves for a login: the bar carries the state.
    if (isPreview()) return;
    global.location.replace(loginUrl());
  }

  /* A signed-in customer whose signup webhook the engine has not processed
   * yet: no subscriber row, but one is on its way. The engine answers 202 with
   * the error envelope's code SIGNUP_PENDING. The code is accepted at the top
   * level or under `data` as well, so a flatter envelope still reads as
   * pending. */
  var SIGNUP_PENDING = 'SIGNUP_PENDING';
  function signupPendingBody(envelope) {
    if (!envelope || typeof envelope !== 'object') return false;
    if (envelope.code === SIGNUP_PENDING) return true;
    if (envelope.error && envelope.error.code === SIGNUP_PENDING) return true;
    return !!(envelope.data && envelope.data.code === SIGNUP_PENDING);
  }

  function normalise(status, body, method) {
    var envelope = body || {};
    // A pending signup carries no member data, so it is never ok: no caller
    // can read the 202 body as a payload or keep it. twc-member-data.js
    // turns the code into its signupPending state and polls.
    if (status === 202 && signupPendingBody(envelope)) {
      var pendingErr = envelope.error || {};
      return { ok: false, status: status, code: SIGNUP_PENDING, message: pendingErr.message || envelope.message || null, data: null };
    }
    if (status >= 200 && status < 300) {
      return { ok: true, status: status, code: null, message: null, data: envelope.data != null ? envelope.data : envelope };
    }
    var err = envelope.error || {};
    // "No open campaign" 404s are a normal member state, not a failure — for
    // READS only. A 404 on a mutation means the write DID NOT HAPPEN (wrong
    // id, removed record): mapping it to ok would close a dialog over a
    // silent no-op, so mutations fall through to the failure branch.
    if (status === 404 && err.code === 'NOT_FOUND' && (method === undefined || method === 'GET')) {
      return { ok: true, status: status, code: 'NOT_FOUND', message: err.message || null, data: null, noCampaign: true };
    }
    return { ok: false, status: status, code: err.code || 'HTTP_' + status, message: err.message || null, data: null };
  }

  function missingFixture(path) {
    return { ok: true, status: 404, code: 'NOT_FOUND', message: 'No fixture for ' + path, data: null, noCampaign: true };
  }

  function fixtureSlice(responses, key) {
    var entry = responses[key];
    return entry ? normalise(entry.status, entry.body) : missingFixture(key);
  }

  /* DEV TOOLING ONLY — never a live fallback.
   *
   * A fixture bundle may hold the seven SLICE responses and no `dashboard`
   * entry. This stitches the composed body out of the slices the bundle has —
   * the same mapping the engine performs, key for key. A bundle that DOES
   * carry a `dashboard` entry is used verbatim and never reaches this function.
   *
   * The 404 mapping each slice already has is honoured: a 404 box_current is
   * the no-campaign state, so it lands as box_current:null + noCampaign:true,
   * which is exactly what the live composed GET answers (200, never a 404). A
   * failing `me` (the 401 bundles) fails the whole composed request, as it
   * would on the storefront.
   */
  function synthesizeDashboard(bundle) {
    var responses = bundle.responses || {};
    var me = fixtureSlice(responses, 'me');
    if (!me.ok || !me.data) return me;
    var box = fixtureSlice(responses, 'box_current');
    var topup = fixtureSlice(responses, 'topup_current');
    return {
      ok: true,
      status: 200,
      code: null,
      message: null,
      data: {
        asOf: bundle.capturedAt || null,
        noCampaign: !!box.noCampaign,
        me: me.data,
        campaigns_current: fixtureSlice(responses, 'campaigns_current').data,
        // box_current carries no `grids` key in an old bundle; leave it absent
        // rather than inventing one — the grid island reads that as hidden.
        box_current: box.data,
        topup_current: topup.data,
        delivery_prefs: fixtureSlice(responses, 'delivery_prefs').data,
        ballots: fixtureSlice(responses, 'ballots').data,
        referrals: fixtureSlice(responses, 'referrals').data,
      },
    };
  }

  function fixtureRequest(method, path) {
    return loadFixtureBundle().then(function (bundle) {
      if (method !== 'GET') {
        return { ok: false, status: 0, code: 'FIXTURE_MODE', message: 'Mutations are disabled in fixture mode', data: null };
      }
      var key = fixtureKeyFor(path);
      var entry = key ? bundle.responses[key] : null;
      if (!entry) {
        if (key === 'dashboard') return synthesizeDashboard(bundle);
        return missingFixture(path);
      }
      // The one orders_detail entry can carry a per-order map. A bundle keys
      // bodies by the id in the path (`byId`), so each fixture order opens its
      // OWN detail; an id outside the map is the not-found path, which keeps
      // the detail-failure copy testable. The flat {status, body} pair stays
      // the fallback for a bundle without a map.
      if (key === 'orders_detail' && entry.byId) {
        var orderId = path.slice('/orders/'.length).split('?')[0];
        var mapped = entry.byId[orderId];
        if (mapped) return normalise(mapped.status, mapped.body);
        if (entry.status && entry.body) return normalise(entry.status, entry.body);
        return missingFixture(path);
      }
      return normalise(entry.status, entry.body);
    });
  }

  /* One-shot handover from snippets/twc-dashboard-prefetch.liquid.
   *
   * That inline snippet starts the cold-load GETs of the page while the
   * browser parses the HTML, long before this deferred script runs. It parks
   * each fetch promise in the path-keyed map window.__TWC_PREFETCH — always
   * `/dashboard`, plus the one page-specific path (`/orders` on the orders
   * page, `/payment/methods` on the personal-details page). The cold load
   * takes the parked promise here instead of making a second request.
   * window.__TWC_DASH_PREFETCH is the same object as the `/dashboard` entry,
   * thus an older render site that sets only that global still works.
   *
   * The handover is ONE-SHOT PER PATH: `consumed` latches on the first take,
   * thus TwcMemberData.refresh() (settle poll, pause, cancel, tier) and every
   * later request always make a real network request. The base must also
   * agree with apiBase(): a page that changed the base after the prefetch
   * started must not read the old API's answer. A fixture or QA mode takes
   * nothing: the snippet parks nothing in those modes, and the canned bundle
   * is the only source there.
   *
   * The value is the raw fetch promise (a Response), not a normalised result.
   * Islands do not need it: api.get(path) consumes the park automatically.
   * The function is public because a lane can hold the promise before it does
   * its own work with it.
   */
  function consumePrefetch(path) {
    if (isFixtureMode()) return null;
    // The snippet parks nothing in a preview tab; a park from any other
    // source would carry no token, so a preview never takes one.
    if (isPreview()) return null;
    var map = global.__TWC_PREFETCH;
    var parked = (map && map[path]) || (path === '/dashboard' ? global.__TWC_DASH_PREFETCH : null);
    if (!parked || parked.consumed || !parked.promise) return null;
    if (parked.base !== apiBase()) return null;
    parked.consumed = true;
    return parked.promise;
  }

  function request(method, path, opts) {
    opts = opts || {};
    if (isFixtureMode()) return fixtureRequest(method, path);

    var preview = previewEntry();
    if (preview) {
      if (method !== 'GET') return Promise.resolve(readOnlyResult());
      if (preview.expired || previewExpiredSeen) {
        if (!previewWorked()) {
          dropPreview();
          return Promise.resolve({ ok: false, status: 401, code: 'PREVIEW_EXPIRED', message: null, data: null });
        }
        notifyPreviewExpired();
        return Promise.resolve({ ok: false, status: 401, code: 'PREVIEW_EXPIRED', message: null, data: null });
      }
    }

    var init = {
      method: method,
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' },
    };
    var dev = preview ? null : devSubscriber();
    if (dev) init.headers['x-twc-subscriber-id'] = dev;
    if (opts.body !== undefined) {
      init.headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(opts.body);
    }
    var url = apiBase() + path;
    var query = '';
    if (opts.params) {
      var qs = new URLSearchParams();
      Object.keys(opts.params).forEach(function (k) {
        var v = opts.params[k];
        if (v != null && v !== '') qs.append(k, v);
      });
      query = qs.toString();
      if (query) url += (url.indexOf('?') === -1 ? '?' : '&') + query;
    }
    if (preview) {
      url += (url.indexOf('?') === -1 ? '?' : '&') + 'twc_preview=' + encodeURIComponent(preview.token);
    }
    // One response pipeline for both sources. A parked prefetch response and a
    // fresh response get identical handling — parse, normalise, 401 bounce and
    // network-failure shape.
    function handle(pending) {
      return pending
        .then(function (res) {
          var ct = res.headers.get('content-type') || '';
          var parse = ct.indexOf('application/json') !== -1
            ? res.json().catch(function () { return null; })
            : res.text().catch(function () { return null; }).then(function (t) { return t ? { error: { code: 'HTTP_' + res.status, message: t.slice(0, 200) } } : null; });
          return parse.then(function (body) {
            var r = normalise(res.status, body, method);
            if (preview) {
              // A preview never bounces to login and never reads as a
              // signed-in non-member: the token expired or was refused.
              if (r.ok) {
                markPreviewWorked();
              } else if (r.status === 401 || (r.status === 403 && r.code === 'PREVIEW_EXPIRED')) {
                if (!previewWorked()) {
                  dropPreview();
                  return r;
                }
                notifyPreviewExpired();
              } else if (r.status === 403) {
                flash(previewCopy('read_only', 'Read-only preview'), 'info');
              }
              return r;
            }
            if (r.status === 401 && !dev) {
              // Logged-out member on the storefront: bounce to login. A
              // SIGNED-IN customer answering 401 is different: the engine has
              // no subscriber row for them (registered non-member), and the
              // login they would be bounced to is one they already passed —
              // a login loop. window.TWC_LOGGED_IN is stamped by
              // snippets/twc-dashboard-prefetch.liquid exactly when Liquid
              // rendered the page for a customer; when it is set the 401
              // resolves in place and twc-member-data.js broadcasts the
              // NO_MEMBERSHIP state instead.
              if (!global.TWC_LOGGED_IN) redirectToLogin();
            }
            return r;
          });
        })
        .catch(function (e) {
          return { ok: false, status: 0, code: 'NETWORK', message: String(e && e.message || e), data: null };
        });
    }

    /* A parked promise answers the BARE path. A request that adds a query
     * (a second orders page, for example) asks a different question, thus it
     * makes its own request and leaves the park for the first caller. */
    var prefetched = (method === 'GET' && !query) ? consumePrefetch(path) : null;
    return handle(prefetched || fetch(url, init));
  }

  function get(path, params) { return request('GET', path, { params: params }); }
  function post(path, body) { return request('POST', path, { body: body }); }

  var moneyFormatters = {};
  function money(cents, currency) {
    if (cents == null || isNaN(cents)) return '';
    var cur = currency || 'AUD';
    if (!moneyFormatters[cur]) {
      try {
        // narrowSymbol: members see "$120.00" for AUD (design), not "A$120.00".
        moneyFormatters[cur] = new Intl.NumberFormat(undefined, { style: 'currency', currency: cur, currencyDisplay: 'narrowSymbol' });
      } catch (e) {
        moneyFormatters[cur] = { format: function (n) { return '$' + n.toFixed(2); } };
      }
    }
    return moneyFormatters[cur].format(cents / 100);
  }

  // "12 Aug 2026" — readable, unambiguous for AU/NZ members. A store-local
  // calendar date ("2026-08-12") prints verbatim from its parts; an ISO
  // instant formats through the browser clock.
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  function date(value) {
    if (!value) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) return calendarDate(value);
    var d = new Date(value);
    if (isNaN(d.getTime())) return String(value);
    return d.getDate() + ' ' + MONTHS[d.getMonth()] + ' ' + d.getFullYear();
  }

  // Store-local CALENDAR date ("2026-08-14") rendered VERBATIM in the same
  // "14 Aug 2026" shape. Parsed from the string parts, never through
  // `new Date()`: a date-only value has no instant, so any Date parse would
  // read it as UTC midnight and print the day before west of UTC. A full ISO
  // instant still matches on its leading YYYY-MM-DD and reads as the store's
  // day, which keeps theme and engine deploy order independent.
  function calendarDate(value) {
    if (!value) return '';
    var m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
    if (!m) return String(value);
    return +m[3] + ' ' + (MONTHS[+m[2] - 1] || m[2]) + ' ' + m[1];
  }

  function escapeHtml(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /* Flash region — the one place member-facing failures are spoken.
   *
   * snippets/twc-account-flash.liquid renders the region on the account
   * pages; anywhere else (the harness, the PDP, a page whose shell does not
   * render it) it is created on demand rather than swallowing the message —
   * a flash that nothing renders is a silent failure.
   *
   * It is a live region, so it is never removed or `hidden` once created:
   * emptying it hides it by CSS (.twc-flash:empty) and keeps it announceable.
   * Messages clear themselves after FLASH_MS; flash('') clears immediately.
   */
  var FLASH_MS = 10000;
  var flashTimer = null;

  function flashRegion() {
    var el = document.getElementById('twc-account-flash');
    if (el || !global.document.body) return el;
    el = global.document.createElement('div');
    el.id = 'twc-account-flash';
    el.className = 'twc-flash';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    global.document.body.appendChild(el);
    return el;
  }

  function flash(message, kind) {
    var el = flashRegion();
    if (!el) return;
    if (flashTimer) { clearTimeout(flashTimer); flashTimer = null; }
    el.textContent = message || '';
    el.dataset.kind = message ? (kind || '') : '';
    if (message) flashTimer = setTimeout(function () { flash('', ''); }, FLASH_MS);
  }

  // Shopify's `t` filter HTML-escapes locale output, so section-emitted copy
  // JSON arrives with entities (&#39; &amp; …). Decode once at parse time —
  // islands render via textContent/escapeHtml, so decoded text stays safe.
  var entityEl = null;
  function decodeEntities(str) {
    if (typeof str !== 'string' || str.indexOf('&') === -1) return str;
    if (!entityEl) entityEl = global.document.createElement('textarea');
    entityEl.innerHTML = str;
    return entityEl.value;
  }
  function decodeDeep(v) {
    if (typeof v === 'string') return decodeEntities(v);
    if (Array.isArray(v)) return v.map(decodeDeep);
    if (v && typeof v === 'object') {
      var o = {};
      for (var k in v) { if (Object.prototype.hasOwnProperty.call(v, k)) o[k] = decodeDeep(v[k]); }
      return o;
    }
    return v;
  }

  global.TwcAccountApi = {
    request: request,
    get: get,
    post: post,
    money: money,
    date: date,
    calendarDate: calendarDate,
    decodeEntities: decodeEntities,
    decodeDeep: decodeDeep,
    escapeHtml: escapeHtml,
    flash: flash,
    routes: routes,
    redirectToLogin: redirectToLogin,
    loginUrl: loginUrl,
    isFixtureMode: isFixtureMode,
    setDesignFixture: setDesignFixture,
    consumePrefetch: consumePrefetch,
    apiBase: apiBase,
    devSubscriber: devSubscriber,
    preview: {
      active: isPreview,
      expired: previewExpired,
      token: function () { var e = previewEntry(); return e ? e.token : null; },
      clear: clearPreview,
      copy: previewCopy,
    },
  };
})(window);
